import type { EntryId, EntryKind } from "@beresta/core";
import { type ReactNode, Suspense, createContext, lazy, useCallback, useContext, useMemo, useState } from "react";

/**
 * Шторка B1 — одне вікно на всі входи: «Писати» в панелі, дотик по запису
 * в дні, стрічці, нотатках, інбоксі, пошуку й на сторінці людини.
 *
 * Контекст, а не маршрут: шторка лежить **поверх** поточного екрана, і
 * під нею має лишатися те, звідки її відкрили. Маршрут забрав би екран.
 *
 * «Писати» бере рід і дату з контексту (WindowMap): із дня — запис того
 * дня, із нотаток — нотатка, звідусіль інде — інбокс. Здогад ніколи не
 * мовчазний: рід і дату видно чіпами вгорі шторки.
 */
export type ComposeTarget =
  | { readonly id: EntryId }
  | { readonly kind: EntryKind; readonly date?: string | null };

interface Compose {
  readonly open: (target: ComposeTarget) => void;
  /** Ревізія, що росте щоразу, як шторка щось зберегла: екрани під нею перечитують. */
  readonly revision: number;
}

const ComposeContext = createContext<Compose>({ open: () => {}, revision: 0 });

export const useCompose = (): Compose => useContext(ComposeContext);

// Редактор тягне ProseMirror, Tiptap і Yjs — у холодному старті йому не місце.
const EntrySheet = lazy(() => import("./EntrySheet.js"));

export function ComposeProvider({ children }: { readonly children: ReactNode }) {
  const [target, setTarget] = useState<ComposeTarget | null>(null);
  const [revision, setRevision] = useState(0);
  const open = useCallback((next: ComposeTarget) => setTarget(next), []);
  const value = useMemo(() => ({ open, revision }), [open, revision]);

  return (
    <ComposeContext.Provider value={value}>
      {children}
      {target !== null && (
        <Suspense fallback={null}>
          <EntrySheet
            target={target}
            onSaved={() => setRevision((r) => r + 1)}
            onClose={() => {
              setTarget(null);
              setRevision((r) => r + 1);
            }}
          />
        </Suspense>
      )}
    </ComposeContext.Provider>
  );
}
